import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {ProjectFile} from '../../api/model/projectFile';
import {ProjectFileControllerService} from '../../api/api/projectFileController.service';

/**
 * Laedt die Dateien eines Projekts in der schlanken Tabellen-Variante (`GET .../files/table`).
 *
 * Warum nicht `ProjectFileControllerService.getFiles`: der Endpoint liefert die volle Entity und kostet
 * pro Datei praesignierte S3-URLs, einen eager geladenen Analyse-Lob und Lazy-Nachladen von
 * `children`/`linkedFiles`/`documentClasses` beim Serialisieren -- plus eine Payload, in der jede Datei
 * zusaetzlich in jedem Vorfahren-`children`-Subtree steckt. Fuer die Tabelle wird davon nichts gebraucht.
 *
 * Handgeschrieben statt im generierten `app/api`-Ordner, damit die naechste Client-Generierung
 * (`api-client-generation.sh`) das hier nicht ueberschreibt. Der Auth-Header kommt wie bei den
 * generierten Services aus dem HTTP-Interceptor.
 *
 * Rueckgabetyp ist bewusst `ProjectFile`: die Antwort ist eine echte Teilmenge davon, so bleibt die
 * Typisierung in der Dateienansicht unveraendert. Nicht enthalten und bei Bedarf per
 * `getFile(id)` nachzuladen: `link` (Download), `linkedFiles` (Koordinationsmodelle),
 * `classificationResults`, `children`.
 *
 * `classificationRules` ist enthalten, aber nur mit `id` -- siehe {@link toProjectFile}.
 */
/** Rohform einer Zeile von `GET .../files/table`. */
interface ProjectFileTableRow extends ProjectFile {
  /** IDs der HOAI-Vorklassifizierung; im vollen Endpoint stecken sie in `classificationRules`. */
  classificationRuleIds?: string[];
}

@Injectable({providedIn: 'root'})
export class ProjectFileTableService {
  private http = inject(HttpClient);
  private fileController = inject(ProjectFileControllerService);

  private get basePath(): string {
    return (this.fileController as any).basePath ?? '';
  }

  getTableRows(projectId: number): Observable<ProjectFile[]> {
    return this.http.get<ProjectFileTableRow[]>(
      `${this.basePath}/projects/${encodeURIComponent(String(projectId))}/files/table`,
      {withCredentials: true},
    ).pipe(
      map(rows => rows.map(row => this.toProjectFile(row))),
    );
  }

  /**
   * Der Endpoint liefert die Klassifizierung als reine ID-Liste; die Dateiauswahl der Checks liest sie
   * wie gewohnt aus {@code classificationRules}. Nur die {@code id} wird gesetzt -- der Name der Regel
   * kommt in dieser Projektion nicht mit und wird dort auch nirgends ausgewertet.
   */
  private toProjectFile(row: ProjectFileTableRow): ProjectFile {
    const {classificationRuleIds, ...file} = row;
    if (!classificationRuleIds?.length) {
      return file;
    }
    return {...file, classificationRules: classificationRuleIds.map(id => ({id}))};
  }
}
